import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system-default';

export type ThemeContextValue = {
  darkMode: ThemeMode;
  setDarkMode: (mode: ThemeMode) => void;
};

export const ThemeContext = createContext<ThemeContextValue>({
  darkMode: 'system-default',
  setDarkMode: () => {},
});

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }: React.PropsWithChildren): JSX.Element => {
  const [darkMode, _setDarkMode] = useState<ThemeMode>('system-default');
  
  const updateDocumentMode = (mode: ThemeMode) => {
    let actualMode: 'light' | 'dark';
    
    if (mode === 'system-default') {
      const mm = window.matchMedia('(prefers-color-scheme: dark)');
      actualMode = mm.matches ? 'dark' : 'light';
    } else {
      actualMode = mode;
    }
    
    document.documentElement.setAttribute('data-mode', actualMode);
  };

  const setDarkMode = (mode: ThemeMode, skipLocalStorage = false) => {
    _setDarkMode(mode);
    updateDocumentMode(mode);
    if (!skipLocalStorage) {
      window.localStorage.setItem('theme', mode);
    }
  };

  useEffect(() => {
    const mm = window.matchMedia('(prefers-color-scheme: dark)');
    const theme = window.localStorage.getItem('theme') as ThemeMode | null;
    
    if (theme && ['light', 'dark', 'system-default'].includes(theme)) {
      setDarkMode(theme, true);
    } else {
      // Default to system-default if no valid theme is stored
      setDarkMode('system-default', true);
    }

    // Listen for system theme changes when in system-default mode
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (darkMode === 'system-default') {
        updateDocumentMode('system-default');
      }
    };

    mm.addEventListener('change', handleSystemThemeChange);

    return () => {
      mm.removeEventListener('change', handleSystemThemeChange);
    };
  }, [darkMode]);

  return (
    <ThemeContext.Provider
      value={{
        darkMode,
        setDarkMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};